import { useApi } from '@gravitate-js/the-armory'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getAPIURL } from '@utils/api'
import type { DriverFreightRatesQueryResponse } from './types/response'

export const useDriverFreightRates = () => {
  const api = useApi({ baseURLOverride: getAPIURL() })
  const queryClient = useQueryClient()

  const useDriverFreightRatesQuery = () =>
    useQuery({
      queryKey: ['driverFreightRatesQuery'],
      queryFn: ({}) => api.post('payroll/driver_rate/overview'),
    })

  const driverRatesUpdateMutation = () =>
    useMutation({
      mutationFn: (payload) => api.post('payroll/driver_rate/update', payload),

      onSuccess: async (newDriverRate) => {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries({
          queryKey: ['driverFreightRatesQuery'],
        })
        queryClient.setQueriesData<DriverFreightRatesQueryResponse>(
          { queryKey: ['driverFreightRatesQuery'] },
          (oldData) => {
            return {
              ...oldData,
              rows: oldData.rows.map((rateTable) =>
                rateTable.id === newDriverRate?.id ? { ...rateTable, ...newDriverRate } : rateTable
              ),
            }
          }
        )
      },

      onError: () => {
        // Not an effecient way to handle this, but can't think of another way for now.
        // Ag-Grid uses references for row data instead of copies, so getQueriesData in `onMutate`
        // doesn't work as a caching mechanism as you always get back the cell value after edit
        queryClient.invalidateQueries({
          queryKey: ['driverFreightRatesQuery'],
        })
      },
    })

  const driverFreightSetRatesMutation = useMutation({
    mutationFn: (payload) => api.post('payroll/driver_rate/set_rates', payload),

    onSuccess: async (newDriverRate) => {
      await queryClient.cancelQueries({
        queryKey: ['driverFreightRatesQuery'],
      })
      const previousQueries = queryClient.getQueriesData({
        queryKey: ['driverFreightRatesQuery'],
      })
      queryClient.setQueriesData<DriverFreightRatesQueryResponse>(
        { queryKey: ['driverFreightRatesQuery'] },
        (oldData) => {
          return {
            ...oldData,
            rows: oldData.rows.map((rateTable) =>
              rateTable.id === newDriverRate?._id ? { ...rateTable, ...newDriverRate } : rateTable
            ),
          }
        }
      )
      return { previousQueries, newDriverRate }
    },
  })
  return {
    useDriverFreightRatesQuery,
    driverRatesUpdateMutation,
    driverFreightSetRatesMutation,
  }
}
